import { Banner, Box, Flex, SegmentedControl, Stack, TextLabel } from '@customerio/pluma-components/react';
import { CodeEditor } from '@/components/code-editor';
import { canShowAsRows } from '../utils/event-data';
import { SimpleEventDataEditor } from './simple-event-data-editor';

/**
 * The event's attributes, in whichever of the two editors suits them.
 *
 * A handful of plain values are quicker to write as a grid; anything nested
 * is quicker to write as JSON. Both write the same JSON, so switching is
 * never lossy.
 */

/** Which editor is on screen, spelled as analytics records it. */
export type EventDataEditorType = 'Simple' | 'JSON';

const EDITOR_TYPES = [
  { value: 'Simple', label: 'Simple' },
  { value: 'JSON', label: 'JSON' },
];

/** Named for the grid's own labels as well as the JSON editor's. */
const EVENT_DATA_LABEL = 'Event data (optional)';

/** Tall enough for a few attributes without pushing the footer off screen. */
const JSON_EDITOR_HEIGHT = '128px';

type EventDataEditorProps = {
  data: string;
  onChange: (data: string) => void;
  editorType: EventDataEditorType;
  onEditorTypeChange: (editorType: EventDataEditorType) => void;
  /** Why the data will not be sent, once a send has been tried. */
  error: string | undefined;
  isDisabled: boolean;
};

export function EventDataEditor({
  data,
  onChange,
  editorType,
  onEditorTypeChange,
  error,
  isDisabled,
}: EventDataEditorProps) {
  return (
    <Stack gap="150">
      <Flex justifyContent="flex-end" w="full">
        <SegmentedControl
          name="editor_type"
          ariaLabel="Event data editor type"
          value={editorType}
          options={EDITOR_TYPES}
          onChange={(value: string) => onEditorTypeChange(value === 'JSON' ? 'JSON' : 'Simple')}
        />
      </Flex>

      <TextLabel>{EVENT_DATA_LABEL}</TextLabel>

      {editorType === 'JSON' ? (
        // Event attributes are the workspace's own customer data; keep them
        // out of session replay.
        <Box className="fs-mask">
          <CodeEditor
            value={data}
            language="json"
            theme="light"
            height={JSON_EDITOR_HEIGHT}
            readOnly={isDisabled}
            ariaLabel={EVENT_DATA_LABEL}
            automaticLayout
            onChange={onChange}
          />
        </Box>
      ) : canShowAsRows(data) ? (
        <SimpleEventDataEditor initialData={data} isDisabled={isDisabled} onChange={onChange} />
      ) : (
        <Banner
          variant="information"
          description="This event data is not a set of named attributes, so it can only be edited as JSON."
        />
      )}

      {error == null ? null : <Banner variant="critical" description={error} />}
    </Stack>
  );
}
